import {
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { Check, type LucideIcon } from "lucide-react";
import { clsx } from "clsx";
import { pressHandlers } from "./press";
import pressable from "./pressable.module.css";
import styles from "./ChoiceChips.module.css";

/**
 * ChoiceChips: one choice from a set that does not fit a segmented control.
 *
 * Chips wrap freely with an 8px gap; there is no container pill, so the
 * group keeps its shape at 3 options or 30. `role="radiogroup"` with
 * `role="radio"` chips and one roving tab stop. Arrow keys move focus and
 * selection like native radios; Home and End jump to the ends; Space and
 * Enter select the focused chip.
 *
 * Selection is tonal: an accent tint plus a check glyph that fades into a
 * reserved slot, so the chip never changes size. `value` may be `null`
 * (nothing chosen yet); the tab stop then rests on the first enabled chip.
 */

export interface ChoiceChipsOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Second line under the label. Chips with a description grow to two lines. */
  description?: string;
  icon?: LucideIcon;
  disabled?: boolean;
}

export type ChoiceChipsSize = "sm" | "md";

export interface ChoiceChipsProps<T extends string> {
  options: readonly ChoiceChipsOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  size?: ChoiceChipsSize;
  /** Plain-text group name. Use `labelledBy` when a visible label exists. */
  ariaLabel?: string;
  labelledBy?: string;
  /** Id of the text that explains the group. */
  "aria-describedby"?: string;
  disabled?: boolean;
  /** `auto` wraps chips to their content. A number lays out equal-width chips in a grid. */
  columns?: "auto" | number;
  className?: string;
}

const iconSize: Record<ChoiceChipsSize, number> = { sm: 14, md: 16 };

export function ChoiceChips<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  ariaLabel,
  labelledBy,
  "aria-describedby": ariaDescribedBy,
  disabled = false,
  columns = "auto",
  className,
}: ChoiceChipsProps<T>): ReactElement {
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedIndex = value === null ? -1 : options.findIndex((option) => option.value === value);

  const isEnabled = (index: number): boolean => {
    const option = options[index];
    return option !== undefined && option.disabled !== true && !disabled;
  };

  const select = (index: number): void => {
    const option = options[index];
    if (option === undefined || !isEnabled(index)) return;
    if (option.value !== value) onChange(option.value);
  };

  const stepEnabled = (from: number, direction: 1 | -1): number => {
    const count = options.length;
    const start = from < 0 ? (direction === 1 ? -1 : 0) : from;
    for (let step = 1; step <= count; step += 1) {
      const next = (start + direction * step + count * step) % count;
      if (isEnabled(next)) return next;
    }
    return from;
  };

  const edgeEnabled = (direction: 1 | -1): number => {
    const indices = options.map((_, i) => i);
    if (direction === -1) indices.reverse();
    return indices.find(isEnabled) ?? selectedIndex;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const focused = chipRefs.current.indexOf(event.target as HTMLButtonElement);
    const from = focused >= 0 ? focused : selectedIndex;
    let next: number;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = stepEnabled(from, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = stepEnabled(from, -1);
        break;
      case "Home":
        next = edgeEnabled(1);
        break;
      case "End":
        next = edgeEnabled(-1);
        break;
      default:
        return;
    }
    event.preventDefault();
    if (next < 0) return;
    chipRefs.current[next]?.focus({ preventScroll: true });
    select(next);
  };

  const tabStopIndex = isEnabled(selectedIndex)
    ? selectedIndex
    : options.findIndex((_, i) => isEnabled(i));

  const gridStyle: CSSProperties | undefined =
    typeof columns === "number"
      ? { gridTemplateColumns: `repeat(${String(columns)}, minmax(0, 1fr))` }
      : undefined;

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled || undefined}
      className={clsx(styles.root, className)}
      data-size={size}
      data-layout={typeof columns === "number" ? "grid" : "wrap"}
      data-disabled={disabled || undefined}
      style={gridStyle}
      onKeyDown={handleKeyDown}
    >
      {options.map((option, index) => {
        const selected = index === selectedIndex;
        const inert = !isEnabled(index);
        const Icon = option.icon;
        const hasDescription = option.description !== undefined && option.description.length > 0;
        return (
          <button
            key={option.value}
            ref={(el) => {
              chipRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={index === tabStopIndex ? 0 : -1}
            disabled={inert}
            className={clsx(pressable.pressable, styles.chip)}
            data-selected={selected || undefined}
            data-has-description={hasDescription || undefined}
            onClick={() => select(index)}
            {...pressHandlers<HTMLButtonElement>({}, inert)}
          >
            <span className={styles.glyph} aria-hidden="true">
              <Check size={iconSize[size]} strokeWidth={2.5} />
            </span>
            {Icon !== undefined && (
              <Icon
                className={styles.icon}
                size={iconSize[size]}
                strokeWidth={2.25}
                aria-hidden="true"
              />
            )}
            <span className={styles.text}>
              <span className={styles.label}>{option.label}</span>
              {hasDescription && <span className={styles.description}>{option.description}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
